// client/src/components/AdminRoadmap.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { firestore } from '../../firebase';
import '../CSS/Dashboard.css';
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes, FaSearch } from 'react-icons/fa';
import AdminNavbar from './AdminNavbar';

const STATUSES = [
  { key: 'prod', label: 'En production' },
  { key: 'improve', label: 'Améliorations' },
  { key: 'upcoming', label: 'À venir' },
];

const emptyForm = {
  title: '',
  details: '',
  status: 'upcoming',
  priority: 3,
  tags: '',
  assignedTo: '',
};

const byPriorityThenDate = (a, b) => {
  const pa = Number(a.priority ?? 99);
  const pb = Number(b.priority ?? 99);
  if (pa !== pb) return pa - pb;
  const da = a.createdAt?.toMillis?.() || 0;
  const db = b.createdAt?.toMillis?.() || 0;
  return db - da;
};

// Couleurs priorité (1 bordeaux → 5 jaune)
const priorityBg = (p) => {
  switch (Number(p)) {
    case 1: return '#7B1E1E';   // bordeaux
    case 2: return '#B74A4A';   // rouge plus léger
    case 3: return '#D48A1E';   // orange moyen
    case 4: return '#E5B94E';   // orange clair
    case 5: return '#EFD86A';   // jaune doux
    default: return 'rgba(0,0,0,0.12)';
  }
};
const priorityFg = (p) => (Number(p) === 1 || Number(p) === 2) ? '#FFFFFF' : '#222222';

// Tri uniquement par date (plus récent d'abord)
const byDateDesc = (a, b) => {
  const da = a.createdAt?.toMillis?.() || 0;
  const db = b.createdAt?.toMillis?.() || 0;
  return db - da;
};

const AdminRoadmap = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // Modale de demande de priorité quand on sort de "prod"
  const [priorityModal, setPriorityModal] = useState({
    open: false,
    itemId: null,
    targetStatus: null,
    chosen: 3,
  });

  // Responsive cols
  const [cols, setCols] = useState(1);
  useEffect(() => {
    const computeCols = () => {
      const w = window.innerWidth;
      if (w >= 1200) setCols(3);
      else if (w >= 768) setCols(2);
      else setCols(1);
    };
    computeCols();
    window.addEventListener('resize', computeCols);
    return () => window.removeEventListener('resize', computeCols);
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    const colRef = collection(firestore, 'roadmap');
    const qy = query(colRef, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(qy, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setItems(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const resetForm = () => setForm(emptyForm);
  const parseTags = (raw) => String(raw || '').split(',').map(t => t.trim()).filter(Boolean);
  const canEdit = true; // adapte selon tes rôles

  const createItem = async (e) => {
    e.preventDefault();
    const isProd = (form.status || 'upcoming') === 'prod';
    const payload = {
      title: String(form.title || '').trim(),
      details: String(form.details || '').trim(),
      status: form.status || 'upcoming',
      // Pas de priorité en prod (on n'affiche pas), on peut garder previousPriority vide
      priority: isProd ? null : Number(form.priority || 3),
      tags: parseTags(form.tags),
      assignedTo: String(form.assignedTo || '').trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdByUid: user?.uid || null,
    };
    if (!payload.title) return;
    await addDoc(collection(firestore, 'roadmap'), payload);
    resetForm();
    setShowCreate(false);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (id, draft) => {
    const isProd = (draft.status || 'upcoming') === 'prod';
    const payload = {
      title: String(draft.title || '').trim(),
      details: String(draft.details || '').trim(),
      status: draft.status || 'upcoming',
      // Pas de priorité en prod ; sinon valeur numérique
      priority: isProd ? null : Number(draft.priority || 3),
      tags: String(draft.tags || '')
        .split(',')
        .map(t => t.trim())
        .filter(Boolean),
      assignedTo: String(draft.assignedTo || '').trim(),
      updatedAt: serverTimestamp(),
    };
    if (!payload.title) return;
    await updateDoc(doc(firestore, 'roadmap', id), payload);
    cancelEdit();
  };

  const removeItem = async (id) => {
    if (!window.confirm('Supprimer cet élément de la roadmap ?')) return;
    await deleteDoc(doc(firestore, 'roadmap', id));
  };

  // Recherche + filtres en mémoire
  const filtered = useMemo(() => {
    let list = [...items];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((it) => {
        const txt = [
          it.title || '',
          it.details || '',
          (it.tags || []).join(' '),
          it.assignedTo || '',
        ].join(' ').toLowerCase();
        return txt.includes(q);
      });
    }
    if (statusFilter !== 'all') list = list.filter((it) => (it.status || 'upcoming') === statusFilter);
    if (priorityFilter !== 'all') list = list.filter((it) => String(it.priority ?? '') === String(priorityFilter));
    return list;
  }, [items, search, statusFilter, priorityFilter]);

  const byStatus = useMemo(() => {
    const map = {};
    STATUSES.forEach(s => { map[s.key] = []; });
    filtered.forEach((it) => {
      const s = it.status || 'upcoming';
      if (!map[s]) map[s] = [];
      map[s].push(it);
    });
    // Tri : en "prod" -> par date uniquement ; ailleurs -> priorité puis date
    Object.keys(map).forEach(k => {
      if (k === 'prod') map[k].sort(byDateDesc);
      else map[k].sort(byPriorityThenDate);
    });
    return map;
  }, [filtered]);

  // —— Drag & Drop : désactivé pendant création OU édition ——
  const dndEnabled = !showCreate && !editingId;

  const dndHandlersForColumn = (status) => dndEnabled ? ({
    onDragOver: (e) => { e.preventDefault(); },
    onDrop: async (e) => {
      e.preventDefault();
      const itemId = e.dataTransfer.getData('text/plain');
      if (!itemId) return;
      const item = items.find(i => i.id === itemId);
      if (!item) return;

      // Vers "En production" : on mémorise la priorité dans previousPriority et on met priority=null
      if (status === 'prod') {
        const prev = (typeof item.priority === 'number')
          ? item.priority
          : (typeof item.previousPriority === 'number' ? item.previousPriority : 3);
        await updateDoc(doc(firestore, 'roadmap', itemId), {
          status: 'prod',
          previousPriority: prev,
          priority: null,
          updatedAt: serverTimestamp(),
        });
        return;
      }

      // Vers "Amélioration" ou "À venir"
      // On essaie de récupérer une priorité existante
      let newPriority = null;
      if (typeof item.priority === 'number') {
        newPriority = item.priority;
      } else if (typeof item.previousPriority === 'number') {
        newPriority = item.previousPriority;
      }

      if (newPriority === null) {
        // Pas de donnée de priorité (cas: item créé en prod) -> ouvrir la modale
        setPriorityModal({
          open: true,
          itemId,
          targetStatus: status,
          chosen: 3,
        });
        return;
      }

      // On a une priorité valide -> update direct (sans undefined)
      await updateDoc(doc(firestore, 'roadmap', itemId), {
        status,
        priority: Number(newPriority),
        updatedAt: serverTimestamp(),
      });
    }
  }) : ({});

  const dndPropsForCard = (itemId) => dndEnabled ? ({
    draggable: true,
    onDragStart: (e) => {
      e.dataTransfer.setData('text/plain', itemId);
      e.currentTarget.style.opacity = '0.6';
    },
    onDragEnd: (e) => { e.currentTarget.style.opacity = '1'; },
    style: { cursor: 'grab' }
  }) : ({
    draggable: false,
    style: { cursor: 'default' }
  });

  // ——— UI helpers ———
  const Pill = ({ children, bg, fg }) => (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 10px',
      borderRadius: 999,
      background: bg || 'var(--eb-surface)',
      border: bg ? '1px solid transparent' : '1px solid var(--eb-border)',
      fontSize: 12.5,
      fontWeight: 600,
      color: fg || 'var(--eb-text-2)'
    }}>
      {children}
    </span>
  );

  const fieldStyle = {
    width: '100%',
    minHeight: 42,
    padding: '0 12px',
    borderRadius: 'var(--eb-radius-sm)',
    background: 'var(--eb-surface)',
    fontSize: 14,
  };

  // ===== EditCard avec ÉTAT LOCAL =====
  const EditCard = ({ item, onSave, onCancel }) => {
    const [draft, setDraft] = useState({
      title: item.title || '',
      details: item.details || '',
      status: item.status || 'upcoming',
      priority: Number(item.priority ?? item.previousPriority ?? 3),
      tags: (item.tags || []).join(', '),
      assignedTo: item.assignedTo || '',
    });

    const handleChange = (k) => (e) => {
      const v = e?.target ? e.target.value : e;
      setDraft((d) => ({ ...d, [k]: k === 'priority' ? Number(v) : v }));
    };

    // Empêche drag parasite
    const stopDragOnInputs = {
      draggable: false,
      onDragStart: (e) => { e.stopPropagation(); e.preventDefault(); },
      onMouseDown: (e) => { e.stopPropagation(); },
      onPointerDown: (e) => { e.stopPropagation(); },
      onTouchStart: (e) => { e.stopPropagation(); },
    };

    const isProd = (draft.status || 'upcoming') === 'prod';

    return (
      <div draggable={false} style={{ display: 'grid', gap: 8 }}>
        <input
          {...stopDragOnInputs}
          style={{ ...fieldStyle, fontWeight: 600 }}
          placeholder="Titre"
          value={draft.title}
          onChange={handleChange('title')}
        />
        <textarea
          {...stopDragOnInputs}
          style={{ ...fieldStyle, minHeight: 80, padding: '10px 12px' }}
          placeholder="Détails (optionnel)"
          value={draft.details}
          onChange={handleChange('details')}
        />
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: isProd ? '1fr' : '1fr 1fr' }}>
          <select
            {...stopDragOnInputs}
            style={fieldStyle}
            value={draft.status}
            onChange={handleChange('status')}
          >
            {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>

          {/* Pas de priorité en production */}
          {!isProd && (
            <select
              {...stopDragOnInputs}
              style={fieldStyle}
              value={String(draft.priority)}
              onChange={handleChange('priority')}
            >
              {[1,2,3,4,5].map(n => <option key={n} value={n}>Priorité {n}</option>)}
            </select>
          )}
        </div>
        <input
          {...stopDragOnInputs}
          style={fieldStyle}
          placeholder="Tags (séparés par des virgules)"
          value={draft.tags}
          onChange={handleChange('tags')}
        />
        <input
          {...stopDragOnInputs}
          style={fieldStyle}
          placeholder="Assigné à (optionnel)"
          value={draft.assignedTo}
          onChange={handleChange('assignedTo')}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 4, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onCancel}><FaTimes />&nbsp;Annuler</button>
          <button className="btn" onClick={() => onSave(draft)}><FaSave />&nbsp;Sauver</button>
        </div>
      </div>
    );
  };

  const ViewCard = ({ item, onEdit, onDelete, canEdit }) => {
    const isProd = (item.status || 'upcoming') === 'prod';
    return (
      <div {...dndPropsForCard(item.id)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontWeight: 600, color: 'var(--eb-text)', fontSize: 14.5, overflowWrap: 'anywhere' }}>{item.title}</div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <button className="btn-small" onClick={onEdit} title="Modifier" aria-label="Modifier"><FaEdit /></button>
              <button className="btn-small danger" onClick={onDelete} title="Supprimer" aria-label="Supprimer"><FaTrash /></button>
            </div>
          )}
        </div>

        {item.details && (
          <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', fontSize: 13.5, color: 'var(--eb-text-2)', lineHeight: 1.5 }}>{item.details}</div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {/* Pas de pill priorité en production */}
          {!isProd && typeof item.priority === 'number' && (
            <Pill bg={priorityBg(item.priority)} fg={priorityFg(item.priority)}>
              Priorité {item.priority}
            </Pill>
          )}
          {item.assignedTo && <Pill>Assigné: {item.assignedTo}</Pill>}
          {(item.tags || []).map((t, idx) => <Pill key={idx}>{t}</Pill>)}
        </div>
      </div>
    );
  };

  const Column = ({ status }) => {
    const def = STATUSES.find(s => s.key === status);
    const list = byStatus[status] || [];
    return (
      <div
        {...dndHandlersForColumn(status)}
        className="arm-col"
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
          <h3 className="arm-col__title">{def?.label || status}</h3>
          <span className="eb-badge">{list.length} élément(s)</span>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {list.map((it) => (
            <div key={it.id} className="arm-item">
              {editingId === it.id ? (
                <EditCard
                  item={it}
                  onSave={(draft) => saveEdit(it.id, draft)}
                  onCancel={cancelEdit}
                />
              ) : (
                <ViewCard
                  item={it}
                  onEdit={() => startEdit(it)}
                  onDelete={() => removeItem(it.id)}
                  canEdit={canEdit}
                />
              )}
            </div>
          ))}
          {list.length === 0 && (
            <div style={{ fontSize: 13.5, color: 'var(--eb-muted)', padding: '16px 8px', textAlign: 'center' }}>Aucun élément.</div>
          )}
        </div>
      </div>
    );
  };

  // ===== Modale pour choisir une priorité quand on sort de "prod" =====
  const PriorityModal = () => {
    if (!priorityModal.open) return null;
    const close = () => setPriorityModal({ open: false, itemId: null, targetStatus: null, chosen: 3 });
    const confirm = async () => {
      if (!priorityModal.itemId || !priorityModal.targetStatus) return close();
      await updateDoc(doc(firestore, 'roadmap', priorityModal.itemId), {
        status: priorityModal.targetStatus,
        priority: Number(priorityModal.chosen || 3),
        updatedAt: serverTimestamp(),
      });
      close();
    };
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(15,35,56,.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16
      }}>
        <div className="arm-modal" role="dialog" aria-modal="true">
          <h3 className="arm-modal__title">Choisir une priorité</h3>
          <p style={{ margin: '0 0 8px', color: 'var(--eb-text-2)', fontSize: 14.5 }}>Cet élément sort de <strong>En production</strong>. Sélectionne sa priorité :</p>
          <select
            style={{ ...fieldStyle, minHeight: 44, marginTop: 8 }}
            value={String(priorityModal.chosen)}
            onChange={(e) => setPriorityModal(m => ({ ...m, chosen: Number(e.target.value) }))}
          >
            {[1,2,3,4,5].map(n => <option key={n} value={n}>Priorité {n}</option>)}
          </select>
          <div className="arm-actions" style={{ marginTop: 20 }}>
            <button className="btn ghost" onClick={close}><FaTimes />&nbsp;Annuler</button>
            <button className="btn" onClick={confirm}><FaSave />&nbsp;Valider</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <AdminNavbar />
      <style>{`
        .arm-page { width: 100%; min-width: 0; display: flex; flex-direction: column; gap: 16px; }
        .arm-page .eb-page-header { margin-bottom: 0; align-items: center; }
        .dashboard-container .arm-page .eb-page-title { margin: 0; text-align: left; }
        .arm-page .btn, .arm-modal .btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 40px; padding: 0 14px; border-radius: var(--eb-radius-sm); border: 1px solid transparent; background: var(--eb-brand); color: #fff; font-weight: 600; font-size: 14px; cursor: pointer; box-shadow: var(--eb-shadow-xs); white-space: nowrap; transition: background-color .15s ease; }
        .arm-page .btn:hover, .arm-modal .btn:hover { background: var(--eb-brand-700); }
        .arm-page .btn.ghost, .arm-modal .btn.ghost { background: var(--eb-surface); color: var(--eb-text-2); border-color: var(--eb-border-strong); }
        .arm-page .btn.ghost:hover, .arm-modal .btn.ghost:hover { background: var(--eb-surface-2); color: var(--eb-text); }
        .arm-page .btn-small { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: var(--eb-radius-sm); border: 1px solid transparent; background: transparent; color: var(--eb-muted); cursor: pointer; font-size: 13px; }
        .arm-page .btn-small:hover { background: var(--eb-surface); border-color: var(--eb-border); color: var(--eb-text); }
        .arm-page .btn-small.danger:hover { color: var(--eb-danger); background: var(--eb-danger-bg); border-color: #fbd0cb; }
        .arm-filters { display: flex; gap: 8px; flex-wrap: wrap; }
        .arm-filters > * { flex: 1 1 180px; }
        .arm-filters > .eb-search { flex: 3 1 320px; }
        .arm-create { display: grid; gap: 10px; }
        .arm-create__grid { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
        .arm-col { min-width: 0; background: var(--eb-surface-2); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); padding: 16px; }
        .dashboard-container .arm-col__title { margin: 0; font-size: 15px; font-weight: 650; text-align: left; color: var(--eb-text); }
        .arm-item { border: 1px solid var(--eb-border); border-radius: var(--eb-radius); padding: 12px 14px; background: var(--eb-surface); box-shadow: var(--eb-shadow-xs); }
        .arm-actions { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
        .arm-modal { background: var(--eb-surface); border-radius: var(--eb-radius-xl); padding: 24px; width: 100%; max-width: 420px; box-shadow: var(--eb-shadow-lg); }
        .dashboard-container .arm-modal__title { margin: 0 0 8px; font-size: 18px; font-weight: 650; text-align: left; }
        .arm-footer { font-size: 13px; color: var(--eb-muted); text-align: right; }
        @media (max-width: 600px) {
          .arm-page .eb-page-header > .btn { width: 100%; }
          .arm-actions > .btn { flex: 1 1 100%; }
          .arm-filters > .eb-search { flex-basis: 100%; }
        }
      `}</style>
      <div className="arm-page">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Roadmap Ecobill</h1>
            <p className="eb-page-subtitle">Suivi des évolutions produit</p>
          </div>
          <button
            className="btn"
            onClick={() => setShowCreate(v => !v)}
            aria-expanded={showCreate}
            aria-controls="create-panel"
          >
            <FaPlus />&nbsp;Nouvel élément
          </button>
        </header>

        {/* Filtres rapides (optionnels) */}
        <div className="arm-filters">
          <div className="eb-search">
            <FaSearch className="eb-search__icon" aria-hidden="true" />
            <input
              className="eb-input"
              style={{ ...fieldStyle, paddingLeft: 40 }}
              placeholder="Rechercher…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            style={fieldStyle}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select
            style={fieldStyle}
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="all">Toutes priorités</option>
            {[1,2,3,4,5].map(n => <option key={n} value={n}>Priorité {n}</option>)}
          </select>
        </div>

        {/* Formulaire création (masqué par défaut) */}
        {showCreate && canEdit && (
          <div className="eb-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="eb-modal" role="dialog" aria-modal="true" aria-labelledby="create-panel-title" onClick={(e) => e.stopPropagation()}>
          <div className="eb-modal__head">
            <h2 className="eb-modal__title" id="create-panel-title">Nouvel élément</h2>
            <button type="button" className="eb-modal__close" aria-label="Fermer" onClick={() => setShowCreate(false)}>&times;</button>
          </div>
          <form
            id="create-panel"
            onSubmit={createItem}
            className="arm-create eb-modal__body"
          >
            <input
              style={fieldStyle}
              placeholder="Titre (ex: Fidélité par points)"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
            <textarea
              style={{ ...fieldStyle, minHeight: 70, padding: '10px 12px' }}
              placeholder="Détails (optionnel)"
              value={form.details}
              onChange={(e) => setForm({ ...form, details: e.target.value })}
            />

            <div className="arm-create__grid">
              <select
                style={fieldStyle}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>

              {/* Masquer priorité si "En production" */}
              {(form.status || 'upcoming') !== 'prod' && (
                <select
                  style={fieldStyle}
                  value={String(form.priority)}
                  onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                >
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>Priorité {n}</option>)}
                </select>
              )}

              <input
                style={fieldStyle}
                placeholder="Tags (ex: fidélité, stripe)"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
              />

              <input
                style={fieldStyle}
                placeholder="Assigné à (optionnel)"
                value={form.assignedTo}
                onChange={(e) => setForm({ ...form, assignedTo: e.target.value })}
              />
            </div>

            <div className="arm-actions">
              <button type="button" className="btn ghost" onClick={resetForm}><FaTimes />&nbsp;Réinitialiser</button>
              <button type="submit" className="btn"><FaPlus />&nbsp;Ajouter</button>
            </div>
          </form>
          </div>
          </div>
        )}

        {/* Kanban responsif */}
        <div
          style={{
            display: 'grid',
            gap: 12,
            gridTemplateColumns: cols === 3 ? '1fr 1fr 1fr' : cols === 2 ? '1fr 1fr' : '1fr'
          }}
        >
          {STATUSES.map(s => (
            <Column key={s.key} status={s.key} />
          ))}
        </div>

        <div className="arm-footer">
          {loading ? 'Chargement…' : `${filtered.length} élément(s) visibles`}
        </div>
      </div>

      {/* Modale priorité */}
      <PriorityModal />
    </div>
  );
};

export default AdminRoadmap;
